// Wires the session events to the three screens. This file decides what is on screen and nothing
// else: no protocol knowledge beyond the events, no game rules at all.

import { Session, Phase } from './net/session.js';
import { accountToken, setAccountToken, sessionToken } from './net/identity.js';
import { Accounts, linkFromAddress } from './net/auth.js';
import { EntryForm, RoomView, UNSUPPORTED_GAMES } from './ui/lobby.js';
import { AccountView, AccountMode } from './ui/account.js';
import { StatsView } from './ui/stats.js';
import { RulesView } from './ui/rules.js';
import { ChatPanel } from './ui/chat.js';
import { TableView } from './ui/table.js';
import { TableState } from './game/table-state.js';
import { Pacer } from './game/pacing.js';
import { Vocabulary } from './game/vocabulary.js';
import { el, byId } from './ui/dom.js';

const root = byId('app');
const notices = byId('notices');

let session = null;
let catalogue = null;
// Who is signed in, for the whole app. The entry form and the account screen both draw from it,
// and nothing else in here knows how an account works
const accounts = new Accounts();
let vocabulary = null;
let tableState = null;

// Nothing from the server is drawn the moment it arrives: bots answer instantly and a whole trick
// would land in one frame. The pacer hands the messages over one at a time, at the speed the
// Android app shows them
const pacer = new Pacer({
  apply: (item) => {
    if (item.kind === 'request') {
      tableState?.applyRequest(item.object, item.position, item.forMe);
    } else {
      tableState?.applyNotification(item.object);
    }
    table.render();
    return tableState;
  },
});

const entry = new EntryForm({
  onLoadCatalogue: async (serverUrl) => {
    catalogue = await Session.fetchCatalogue(serverUrl);
    vocabulary = new Vocabulary(catalogue.raw);
    chat.setEmotes(catalogue.emotes);
    return catalogue;
  },
  onCreate: (config) => startSession((s) => s.createRoom(config)),
  onJoin: (config) => startSession((s) => s.joinRoom(config)),
  onStats: (serverUrl) => showStats(serverUrl),
  onRules: (gameName) => {
    rules.show(gameName);
    show(rules);
  },
  onAccount: () => showAccount(),
});

const account = new AccountView({
  onBack: () => backToEntry(),
  // Signing in is not the point of the evening: once it worked, go back to the table
  onLogin: async (credentials) => {
    await accounts.login(entry.serverUrl, credentials);
    backToEntry();
    notice(`Bentornato, ${accounts.userName}`);
    return '';
  },
  onRegister: async (details) => {
    await accounts.register(entry.serverUrl, details);
    backToEntry();
    notice(`Account creato: sei ${accounts.userName}`);
    return '';
  },
  onLogout: async () => {
    await accounts.logout(entry.serverUrl);
    return 'Sei uscito. Puoi sempre giocare come ospite';
  },
  onChangePassword: (passwords) => accounts.changePassword(entry.serverUrl, passwords),
  onSetEmail: (address) => accounts.setEmail(entry.serverUrl, address),
  onRequestReset: (who) => accounts.requestPasswordReset(entry.serverUrl, who),
  onResetPassword: async (reset) => {
    const message = await accounts.resetPassword(entry.serverUrl, reset);
    account.update({ mode: AccountMode.SIGNED_OUT });
    return message;
  },
  onClaimHistory: () => accounts.claimHistory(entry.serverUrl),
  onDeleteAccount: (password) => accounts.deleteAccount(entry.serverUrl, password),
  onStats: () => showStats(entry.serverUrl),
});

// One place redraws everything that depends on who is signed in
accounts.addEventListener('account', () => {
  entry.setAccount(accounts.account);
  account.update({ account: accounts.account, emailEnabled: emailEnabled() });
});

const rules = new RulesView({
  onBack: () => backToEntry(),
});

const stats = new StatsView({
  onBack: () => backToEntry(),
  onAdoptToken: (token) => {
    if (!setAccountToken(token)) {
      notice('Codice non valido');
      return;
    }
    notice('Codice adottato: ora sei quel giocatore');
    showStats(lastServerUrl);
  },
});

let lastServerUrl = null;

async function showStats(serverUrl) {
  lastServerUrl = serverUrl;
  show(stats);
  stats.showLoading();
  try {
    const reply = await Session.fetchStats(serverUrl, accountToken(), sessionToken());
    // The device code is the identity of whoever has no account, and only they are offered it:
    // showing it to somebody signed in would be showing them a second, older self
    stats.show(reply, accountToken(), { signedIn: accounts.signedIn });
  } catch (error) {
    stats.showError(error.message);
  }
}

// Whether this server can send mail at all, which is what decides if recovery by link is offered
function emailEnabled() {
  return catalogue?.emailEnabled ?? accounts.account?.emailEnabled ?? false;
}

function showAccount(mode, resetToken) {
  account.update({
    account: accounts.account,
    emailEnabled: emailEnabled(),
    mode,
    resetToken,
  });
  show(account);
}

function backToEntry() {
  show(entry);
  entry.loadCatalogue();
}

const room = new RoomView({
  onStart: () => session?.startGame(),
  onLeave: () => leave(),
});

const table = new TableView({
  onRespond: (response) => session?.respond(response),
  // The end of a hand is read rather than watched, so the queue waits there until this comes back
  onContinue: () => pacer.release(),
  onLeave: () => {
    // A finished game has nothing left to abandon
    if (tableState?.finished || confirm('Vuoi abbandonare la partita?')) leave();
  },
});

const chat = new ChatPanel({
  onSendText: (text) => session?.sendChatText(text),
  onSendEmote: (name) => session?.sendChatEmote(name),
});

document.body.append(chat.element);

function show(view) {
  root.replaceChildren(view.element);
}

function notice(text) {
  const line = el('p', { className: 'notice', text });
  notices.append(line);
  setTimeout(() => line.remove(), 4000);
}

function startSession(begin) {
  session = new Session();

  session.addEventListener('phase', ({ detail }) => {
    switch (detail.phase) {
      case Phase.LOBBY:
        show(room);
        chat.show();
        room.update({
          roomId: session.roomId,
          players: session.playersNames,
          seats: session.playersNames.length,
          isHost: session.myPosition === 0,
        });
        break;
      case Phase.PLAYING:
        show(table);
        chat.show();
        break;
      case Phase.FINISHED:
        chat.hide();
        pacer.reset();
        tableState = null;
        show(entry);
        entry.loadCatalogue();
        break;
      default:
        break;
    }
  });

  session.addEventListener('lobby', ({ detail }) => {
    // Waiting for the room to fill is the one unhurried moment a game has: the table takes it to
    // fetch its card artwork, so the first deal is not the one that pays for it
    table.prepare(detail.deckType);
    room.update({
      roomId: session.roomId,
      players: detail.players,
      seats: detail.seats,
      isHost: session.myPosition === 0,
    });
  });

  // A reconnection replays game_starting first, so a fresh table is built and the replayed history
  // fills it back in: the same path as a game starting for the first time
  session.addEventListener('game-start', ({ detail }) => {
    // The lobby will not let anyone create a game this client cannot draw, but a room started from
    // the Android app can be joined from here. Saying so and leaving beats sitting at a table where
    // nothing will ever be dealt
    if (UNSUPPORTED_GAMES.has(detail.gameType)) {
      notice("Questa stanza gioca a Scopa, che il browser non sa ancora disegnare: usa l'app");
      leave();
      return;
    }
    pacer.reset();
    tableState = new TableState({
      players: detail.players,
      myPosition: detail.myPosition,
      deckType: detail.deckType,
      gameType: detail.gameType,
      vocabulary,
    });
    table.attach(tableState);
    table.setRebuilding(detail.syncing);
  });

  // Both go through the pacer rather than straight onto the felt: the order they arrive in is the
  // order they are shown in, one at a time. A replayed history is put back at once instead
  session.addEventListener('game-message', ({ detail }) => {
    pacer.push({ kind: 'notification', object: detail.object, silent: detail.syncing });
  });

  session.addEventListener('request', ({ detail }) => {
    pacer.push({
      kind: 'request',
      object: detail.object,
      position: detail.position,
      forMe: detail.forMe,
      silent: detail.syncing,
    });
  });

  session.addEventListener('sync', ({ detail }) => table.setRebuilding(detail.syncing));

  session.addEventListener('chat', ({ detail }) => chat.addMessage(detail.message));
  session.addEventListener('chat-history', ({ detail }) => chat.addHistory(detail.messages));

  session.addEventListener('notice', ({ detail }) => notice(detail.text));
  session.addEventListener('fatal', ({ detail }) => notice(detail.message));

  chat.setEmotes(catalogue?.emotes ?? []);
  begin(session);
}

function leave() {
  session?.leave();
  session = null;
}

show(entry);
start();

// The catalogue first, because it says where the server is and what it can do; then whoever was
// signed in last time; then, if this page was opened from a link in somebody's mail, that link
async function start() {
  await entry.loadCatalogue();
  const link = linkFromAddress();
  await accounts.resume(entry.serverUrl);
  if (accounts.account?.claimableGames) {
    notice('Ci sono partite di questo dispositivo da collegare al tuo account');
  }
  if (!link) return;
  if (link.kind === 'reset') {
    showAccount(AccountMode.RESET, link.token);
    return;
  }
  try {
    notice(await accounts.verifyEmail(entry.serverUrl, link.token));
  } catch (error) {
    notice(error.message);
  }
}
